/**
 * AppRouter — Resolves the active screen to its React component.
 *
 * Extracted from App.js as part of the P0 complexity reduction.
 * Owns: 200+ ModuleId → Screen component mappings (driven by SCREEN_REGISTRIES + factory screens).
 *
 * Props:
 *  - activeScreen        : current screen descriptor ({ id, moduleId, subItemId, title }) | undefined
 *  - theme               : 'light' | 'dark'
 *  - navigateTo          : (moduleId, subItemId?) => void
 *  - closeScreen         : (id) => void
 *  - hubState            : current HubNavigation state
 *  - onHubStateChange    : (state) => void
 *  - globalContextData   : full user/company context bag (role used by HelpEngine etc.)
 */
import React from 'react';
import { ModuleId } from '@/types';
import Breadcrumb from '@/components/Breadcrumb';

import ModuleSelector from '@/components/ModuleSelector';
import RoleAwareDashboard from '@/screens/RoleAwareDashboard';
import DynamicSetupScreen from '@/screens/DynamicSetupScreen';
import ULIDGeneratorScreen from '@/screens/ULIDGeneratorScreen';
import FaultAudit from '@/screens/FaultAudit';
import MicroserviceMesh from '@/screens/MicroserviceMesh';
import IdentityEngine from '@/screens/IdentityEngine';
import LicenseKernel from '@/screens/LicenseKernel';
import InfraGuide from '@/screens/InfraGuide';
import AuthFlow from '@/screens/AuthFlow';

// Finance
import ChartOfAccountsScreen from '@/screens/finance/ChartOfAccountsScreen';
import FinanceSettingsScreen from '@/screens/finance/FinanceSettingsScreen';
import ScheduleMasterScreen from '@/screens/finance/ScheduleMasterScreen';
import PrimaryGroupScreen from '@/screens/finance/PrimaryGroupScreen';
import GroupLedgerScreen from '@/screens/finance/GroupLedgerScreen';
import GeneralLedgerScreen from '@/screens/finance/GeneralLedgerScreen';
import VoucherScreen from '@/screens/finance/VoucherScreen';
import BillsAdjustmentScreen from '@/screens/finance/BillsAdjustmentScreen';
import PDCAdjustmentScreen from '@/screens/finance/PDCAdjustmentScreen';
import MaturedPDCScreen from '@/screens/finance/MaturedPDCScreen';
import PurchaseOrderVoucherScreen from '@/screens/finance/PurchaseOrderVoucherScreen';
import SalesUpdateScreen from '@/screens/finance/SalesUpdateScreen';
import FinanceReportScreen from '@/screens/finance/FinanceReportScreen';
import OpeningBalanceScreen from '@/screens/finance/OpeningBalanceScreen';
import FinancialStatementScreen from '@/screens/finance/FinancialStatementScreen';
import BankReconciliationScreen from '@/screens/finance/BankReconciliationScreen';
import DayBookScreen from '@/screens/finance/DayBookScreen';
import CashBookScreen from '@/screens/finance/CashBookScreen';
import BankBookScreen from '@/screens/finance/BankBookScreen';
import GeneralLedgerReportScreen from '@/screens/finance/GeneralLedgerReportScreen';
import DefaultAccountsScreen from '@/screens/finance/DefaultAccountsScreen';
import ReIndexingVouchersScreen from '@/screens/finance/ReIndexingVouchersScreen';
import PDCReconciliationScreen from '@/screens/finance/PDCReconciliationScreen';

// System
import BusinessDaysScreen from '@/screens/system/BusinessDaysScreen';
import ModuleManagementScreen from '@/screens/system/ModuleManagementScreen';
import UserManagementScreen from '@/screens/system/UserManagementScreen';
import UserPrivilegesScreen from '@/screens/user-management/UserPrivilegesScreen';
import IndustryConfigScreen from '@/screens/system/IndustryConfigScreen';
import AttributeStudioScreen from '@/screens/attribute-studio/AttributeStudioScreen';
import TicketManagementScreen from '@/screens/system/TicketManagementScreen';
import AttributeEngineScreen from '@/screens/attribute-engine/AttributeEngineScreen';
import HelpEngineScreen from '@/screens/help-engine/HelpEngineScreen';
import TierPrivilegeMatrixScreen from '@/screens/admin/TierPrivilegeMatrixScreen';

// Factory-built UAMS/System screens
import {
  UserGroupsMasterScreen,
  UserRolesMasterScreen,
  UserLevelsMasterScreen,
  UserCreationMasterScreen,
  SubModuleMasterScreen,
  ScreenMasterScreen,
  NarrationMasterScreen,
} from '@/screens/system/SystemFactoryScreens';

// ERP / Inventory / Procurement / POS / Foundation
import ItemCreationScreen from '@/screens/erp/Item/ItemCreationScreen.jsx';
// ItemCreationNew archived as reference — routing handled by ItemMasterDynamic
// import ItemCreationNew from '@/screens/erp/Item/ItemCreationNew.jsx';
import ItemMasterDynamic from '@/screens/erp/Item/ItemMasterDynamic.jsx';
import ItemListScreen from '@/screens/erp/Item/ItemListScreen.jsx';
import ItemMasterScreen from '@/screens/procurement/masters/ItemMasterScreen.js';
import PurchaseInvoiceScreen from '@/screens/erp/PurchaseInvoice/PurchaseInvoiceScreen.jsx';
import UserProfilesScreen from '@/screens/user-management/UserProfilesScreen';
import ERPFormBridge from '@/components/formbuilder/ERPFormBridge';

import {
  FloorMasterScreen, CounterMasterScreen, CustomerMasterScreen, SalesmanMasterScreen,
  PaymentMasterScreen, ShiftMasterScreen, PlaceMasterScreen, PromotionMasterScreen,
  TellerMasterScreen, SalesDivisionMasterScreen, BranchWisePromotionsScreen,
  SalesOrderScreen, DeliveryChallanScreen, DCReturnsScreen, SalesInvoiceScreen,
  POSBillingScreen, PosTerminalEmbed, POSSalesSummaryReport, POSBillToBillReport,
} from '@/screens/pos';
import ApparelPOSScreen from '@/screens/pos/ApparelPOSScreen';
import FoodCourtPOSScreen from '@/screens/pos/FoodCourtPOSScreen';
import SupermarketPOSScreen from '@/screens/pos/SupermarketPOSScreen';
import VegCounterPOSScreen from '@/screens/pos/VegCounterPOSScreen';
import PurchaseInvoiceV2Screen from '@/screens/procurement/purchase-invoice/PurchaseInvoiceV2Screen';
import GRNScreen from '@/screens/procurement/grn/GRNScreen';
import GRCScreen from '@/screens/procurement/grc/GRCScreen';
import ProcurementScreen from '@/screens/procurement/shared/ProcurementScreen';

import GroupCompanyScreen from '@/screens/foundation/GroupCompanyScreen';
import CompanyMasterScreen from '@/screens/foundation/CompanyMasterScreen';
import BranchMasterScreen from '@/screens/foundation/BranchMasterScreen';
import WarehouseMasterScreen from '@/screens/foundation/WarehouseMasterScreen';
import CurrencyMasterScreen from '@/screens/foundation/CurrencyMasterScreen';
import EnterpriseAccountScreen from '@/screens/foundation/EnterpriseAccountScreen';

import {
  SupplierMasterScreen, SupplierContractsScreen, AgentMasterScreen, JobberMasterScreen,
  TransporterScreen, TaxGroupMasterScreen, TaxMasterScreen, TailorMasterScreen,
  UOMMasterScreen, SubUOMMasterScreen, PurchaseOfficerMasterScreen, DefaultVerticalsScreen,
  SalesPriceFormulaScreen, ManufacturerMasterScreen, SettingPriceLevelScreen,
  ItemBarcodeGenerationScreen, HSNCodeMasterScreen, DivisionMasterScreen,
  DepartmentMasterScreen, CategoryMasterScreen, SubCategoryScreen, BrandMasterScreen,
  StyleMasterScreen, SizeMasterScreen, ColorMasterScreen, DesignMasterScreen,
  MaterialMasterScreen, MasterPlanScreen, PurchaseOrderScreen, PurchaseOrderCancelScreen,
  PurchaseOrderStatusScreen, POPendingQtyCancelScreen, POCancelReportScreen,
  POSummaryReportScreen, POSearchScreen, POExcessReportScreen, POPendingQtyCancelReportScreen,
  ProcessCreationScreen, ProductionOrderScreen, ProductionEstimationScreen,
  BillOfMaterialScreen, StockOrderScreen as ProcStockOrderScreen, InwardJobWorkScreen,
  IssueToJobberScreen, IssueToJobberNewScreen, ReceiptFromJobberScreen,
  FinishedProductPreparationScreen, InwardWarehouseScreen, WarehouseReturnsScreen,
  IssueForPackagesScreen, FinishedPackingGoodsScreen, WastageMaterialScreen,
  PrintBarcodeLabelScreen, ReprintBarcodeLabelScreen, InwardBarcodeLabelScreen,
  GoodsReceiptsScreen, GRCDirectScreen, GRCGeneralScreen, GRCAgainstPOScreen,
  GoodsReceiptChallanScreen, ItemWisePriceUpdateScreen, JobProcessIssueReportScreen,
  JobberReceiptReportScreen, ProcessLedgerReportScreen, PurchaseDiscountReportScreen,
  PurchaseInvoiceAgstGRCScreen, PurchaseInvoiceAgstPOScreen, PurchaseInvoiceReportScreen,
  PurchaseReturnsRegisterScreen, PurchaseTaxRegisterScreen, MonthlyReturnsVATScreen,
  PurchaseInvoiceWithReturnsScreen, OpeningStockImportReportScreen, PartyBillScreen,
  PartyBillReturnsScreen, PartyBillEntryScreen, PurchaseInvoiceAgstPBScreen, PartyBillReturnsEntryScreen,
  PendingPBCancelScreen, GRCAgstPBScreen, GRCTemplateScreen, GoodsReturnsAgstPOScreen, GoodsReturnsAgstGRCScreen,
  GRCPurchaseDiscountReportScreen, GoodsReturnsPackingRegisterScreen, PurchaseOrderRegisterScreen,
  PurchaseOrderTemplateRegisterScreen, PurchaseOrderCancelRegisterScreen, ServiceInvoiceRegisterScreen,
  StockOrderStatusScreen, IssueToJobberReportScreen, ReceiptFromJobberReportScreen,
  InwardWarehouseReportScreen, FinishedPackingConsumptionScreen, ReturnsFromJobberScreen,
  ReturnsFromJobberReportScreen, PkgInwardWarehouseReportScreen, PkgWarehouseReturnReportScreen,
  PkgIssueStockReportScreen, FinishedPackingGoodsReportScreen, PackingGiftScreen,
  WastageMaterialReportScreen, JobProcessIssueScreen, JobberReceiptScreen, GoodsReceiveReportScreen,
  PartyBillReportScreen, PurchaseInvoiceAgstPBReportScreen, PartyBillReturnReportScreen,
  PendingPBCancelReportScreen, PartyBillStatusReportScreen, PartyBillWiseAgeingReportScreen,
  GRCAgstPBReportScreen,
} from '@/screens/procurement';

import {
  StockVerifyScheduleScreen, StockVerifyEntryScreen, VerifyUpdateStockScreen, ShortageStockScreen,
  ExcessStockScreen, ShortageExcessReportScreen, StockVerifyEntryManualScreen, PhysicalStockPDAScreen,
  PhysicalStockScanReportScreen, ROLSettingsNewScreen, ReOrderLevelReportScreen, PriceUpdateScreen,
  TokenUpdateScreen, BulkProductUpdationScreen, OEMCodeUpdateScreen, GeneralTokenUpdateScreen,
  ItemConversionScreen, ItemConversionReportScreen, TallyIntegrationAgstBillsScreen,
  AssignToFloorRackScreen, HSNCodeUpdateScreen, PricePrintBarcodeScreen, PriceReprintBarcodeScreen,
  StockMovementScreen, MonthlyStockMovementScreen, StockLedgerScreen, ClosingStockScreen,
  StockAnalysisReportScreen, PilotStockSalesReportScreen, BarcodeItemInformationScreen,
  PriceUpdateReportScreen, TokenUpdateReportScreen, BulkProductUpdationReportScreen,
  OEMCodeUpdationReportScreen, PackedItemsStockReportScreen, ClosingStockWithPurchaseScreen,
  ClosingStockAuditorScreen, StockLedgerWithDCScreen, ClosingStockWithDCScreen,
  ClosingStockImageReportScreen, FloorRackReportScreen, FloorWiseClosingStockReportScreen,
  StockTrackingReportScreen, FinancialYearDetailReportScreen, CustomerWiseFastMovingSalesScreen,
  PurchaseSummaryReportsScreen, PurchasePartyInfoScreen, InvoiceWiseOutstandingReportScreen,
  SupplierWiseAgeingReportScreen, SORSupplierWiseSalesReportScreen, CityWiseOutstandingPayablesScreen,
  CityWiseOutstandingReceivablesScreen, OpeningStockImportScreen, OpeningStockImportWithSupplierScreen,
  MaterialReceiptImportDirectScreen, StockExportToBranchScreen, StockReturnFromBranchOfflineScreen,
  StockReturnFromBranchOnlineScreen, StockReturnImportAtWarehouseScreen, StockReturnToWarehouseScreen,
  StockImportFromWarehouseOfflineScreen, StockImportFromWarehouseOnlineScreen,
  BranchOpeningStockImportScreen, SalesExportToWarehouseScreen, SalesExportToExcelScreen,
  SalesImportFromExcelScreen, PhysicalStockExportToWHScreen, BranchStockMovementScreen,
  BranchWiseStockScreen, StockTransferReportScreen, BranchPOSInfoScreen,
  SupplierWisePurchaseOrderReportScreen, BranchBillToBillReportScreen,
  BranchBulkInvoicePrintReportScreen, BranchDaySheetReportScreen, BranchPOSCancelReportScreen,
  BranchTaxRegisterScreen, BranchCustomerSalesReportScreen, BranchProductAgeingScreen,
  BranchFastMovingItemsScreen, BranchEODReportScreen, StockExportToFranchiseScreen,
  StockImportFromFranchiseScreen, StockImportAtFranchiseOnlineScreen, FranchiseBarcodePrintScreen,
  StockReturnAtFranchiseOnlineScreen, FranchiseSalesImportScreen, StockExportReportScreen,
  FranchiseSalesSummaryScreen, FranchiseStockAndSalesReportScreen, FranchiseSalesScreen,
  GroupWiseFranchiseSalesScreen, FranchiseProductProfitabilityScreen, FranchiseInvoiceReportScreen,
  FranchiseMarkdownProfitReportScreen, StockTransferToShopScreen, StockReturnFromShopScreen,
  ShopBarcodePrintScreen, SalesImportFromShopScreen, ShopStockTransferReportScreen,
  SISPOSInfoScreen, SISSalesScreen, ProductProfitabilityScreen, ProductAgeingScreen,
  FastMovingItemsScreen, ExpiryItemsScreen, SupplierPerformanceScreen, PurchaseProfitabilityScreen,
  MRPWisePurchaseSalesReportScreen, SMProductProfitabilityScreen, ProductProfitabilityNewScreen,
  InventoryDivisionMasterScreen, InventorySizeMasterScreen, InventoryStyleMasterScreen,
  InventoryDepartmentMasterScreen, SubCategoryMasterScreen, InventoryUOMMasterScreen,
  InventorySubUOMMasterScreen, ReceiptFromBranchScreen, StockImportFromBranchScreen,
  BranchStockImportFromWarehouseScreen, IssueToWarehouseReportScreen, ReturnFromWarehouseReportScreen,
  ReceiptAtBranchReportScreen, ItemMovementReportScreen, ItemBarcodeWiseStockReportScreen,
  StockAgingReportScreen,
} from '@/screens/inventory';

export default function AppRouter({
  activeScreen,
  theme,
  navigateTo,
  closeScreen,
  hubState,
  onHubStateChange,
  globalContextData,
  isPosMaximized,
  setIsPosMaximized,
}) {
  if (!activeScreen) {
    return <ModuleSelector onSelect={navigateTo} theme={theme} hubState={hubState} onHubStateChange={onHubStateChange} userRole={globalContextData?.role || ''} />;
  }
  const { moduleId, subItemId, title, id: activeId } = activeScreen;
  const close = () => closeScreen(activeId);

  // Screens below already render their own in-body Back button (via onClose/onBack)
  // — skip the universal breadcrumb bar for those to avoid a duplicate control.
  // Every other screen (500+ cases, including any added later) gets one automatically.
  const HAS_OWN_BACK_UI = new Set([
    ModuleId.DASHBOARD, ModuleId.HUB, ModuleId.ULID_GENERATOR, ModuleId.ITEM_CREATION, ModuleId.ITEM_CREATION_NEW, ModuleId.ITEM_LIST, ModuleId.ITEM_MASTER,
    ModuleId.PURCHASE_INVOICE, ModuleId.GRN, ModuleId.PURCHASE_RETURN, ModuleId.USER_GROUPS, ModuleId.USER_ROLES,
    ModuleId.USER_LEVELS, ModuleId.USER_CREATION, ModuleId.INDUSTRY_CONFIG, ModuleId.ATTRIBUTE_STUDIO,
    ModuleId.DYNAMIC_ATTRIBUTES, ModuleId.HELP_ENGINE, ModuleId.TIER_PRIVILEGE_MATRIX, ModuleId.MODULE_MASTER,
    ModuleId.SUBMODULE_MASTER, ModuleId.SCREEN_MASTER,
  ]);
  const screenEl = resolveScreen();
  // DynamicSetupScreen (the `default:` catch-all covering hundreds of sidebar
  // sub-screens) already renders its own ScreenBackHeader/Back button —
  // checking the resolved element's type (rather than enumerating every
  // sub-screen id) keeps this correct with zero maintenance as more are added.
  const usesOwnBackUI = HAS_OWN_BACK_UI.has(moduleId) || screenEl?.type === DynamicSetupScreen
    || screenEl?.type === VoucherScreen          // VoucherScreen renders its own erp-screen-header with Back
    || screenEl?.type === PDCAdjustmentScreen    // Has own ERPFinanceGrid title header
    || screenEl?.type === MaturedPDCScreen       // Has own title header
    || screenEl?.type === BillsAdjustmentScreen  // Has own title header
    || screenEl?.type === SalesUpdateScreen      // Has own ERPFinanceGrid title header
    || screenEl?.type === PurchaseOrderVoucherScreen // Has own title header
    || screenEl?.type === FinanceReportScreen    // Has own erp-screen-header
    || screenEl?.type === FinancialStatementScreen // Has own erp-screen-header
    || screenEl?.type === OpeningBalanceScreen   // Has own erp-screen-header
    || screenEl?.type === DefaultAccountsScreen  // Has own erp-screen-header
    || screenEl?.type === ReIndexingVouchersScreen // Has own erp-screen-header
    || screenEl?.type === PDCReconciliationScreen // Has own erp-screen-header
    || screenEl?.type === ChartOfAccountsScreen  // Finance master — has own erp-screen-header
    || screenEl?.type === GeneralLedgerScreen    // Finance master — has own erp-screen-header
    || screenEl?.type === GroupLedgerScreen      // Finance master — has own erp-screen-header
    || screenEl?.type === PrimaryGroupScreen     // Finance master — has own erp-screen-header
    || screenEl?.type === ScheduleMasterScreen   // Finance master — has own erp-screen-header
    || screenEl?.type === FinanceSettingsScreen  // Finance global settings — has own erp-screen-header
    // Industry POS screens — have their own function rail with Exit button + status bar navigation
    || screenEl?.type === ApparelPOSScreen
    || screenEl?.type === SupermarketPOSScreen
    || screenEl?.type === VegCounterPOSScreen
    || screenEl?.type === FoodCourtPOSScreen
    // Purchase Invoice / GRN / GRC / All procurement forms — own UI
    || screenEl?.type === PurchaseInvoiceV2Screen
    || screenEl?.type === GRNScreen
    || screenEl?.type === GRCScreen
    || screenEl?.type === ProcurementScreen
    || (isPosMaximized && [ModuleId.POS_BILLING, ModuleId.CASH_COUNTER_BILLING, ModuleId.SALES_INVOICE_RETURNS, ModuleId.SALES_INVOICE].includes(moduleId));
  if (usesOwnBackUI) {
    return screenEl;
  }
  // Inject onBack={close} into the resolved screen element so every
  // ERPAdvancedGrid / ERPMasterScreen automatically gets "← Title" heading.
  // No separate back bar is rendered — the form title IS the navigation control.
  const screenWithBack = React.cloneElement(screenEl, { onBack: close });
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column', minHeight:0 }}>
        {screenWithBack}
      </div>
    </div>
  );

  function resolveScreen() {
  switch (moduleId) {
    case ModuleId.DASHBOARD: return <RoleAwareDashboard theme={theme} onNavigate={navigateTo} />;
    case ModuleId.FAULT_AUDIT: return <FaultAudit theme={theme} />;
    case ModuleId.INFRA_MESH: return <MicroserviceMesh theme={theme} />;
    case ModuleId.IDENTITY: return <IdentityEngine theme={theme} />;
    case ModuleId.LICENSE_KERNEL: return <LicenseKernel theme={theme} />;
    case ModuleId.AUTH_GATEWAY: return <AuthFlow onSuccess={() => {}} theme={theme} />;
    case ModuleId.INFRA_GUIDE: return <InfraGuide theme={theme} />;
    case ModuleId.ULID_GENERATOR: return <ULIDGeneratorScreen theme={theme} onClose={close} />;

    // HUB
    case ModuleId.HUB: return <ModuleSelector onSelect={navigateTo} theme={theme} hubState={hubState} onHubStateChange={onHubStateChange} userRole={globalContextData?.role || ''} />;

    // ─── Finance ───
    case ModuleId.CHART_OF_ACCOUNTS: return <ChartOfAccountsScreen theme={theme} navigateTo={navigateTo} />;
    case ModuleId.FINANCE_SETTINGS:  return <FinanceSettingsScreen  theme={theme} navigateTo={navigateTo} />;
    case ModuleId.SCHEDULE_MASTER: return <ScheduleMasterScreen theme={theme} navigateTo={navigateTo} />;
    case ModuleId.PRIMARY_GROUP: return <PrimaryGroupScreen theme={theme} navigateTo={navigateTo} />;
    case ModuleId.GROUP_LEDGER: return <GroupLedgerScreen theme={theme} navigateTo={navigateTo} />;
    case ModuleId.GENERAL_LEDGER: return <GeneralLedgerScreen theme={theme} navigateTo={navigateTo} />;
    case ModuleId.NARRATION_MASTER: return <NarrationMasterScreen theme={theme} />;
    case ModuleId.DEFAULT_ACCOUNTS: return <DefaultAccountsScreen theme={theme} />;
    case ModuleId.REINDEX_VOUCHERS: return <ReIndexingVouchersScreen theme={theme} />;
    case ModuleId.OPENING_BALANCE: return <OpeningBalanceScreen theme={theme} />;
    case ModuleId.CASH_RECEIPT: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="cash-receipts" title="Cash Receipt" />;
    case ModuleId.BANK_RECEIPT: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="bank-receipts" title="Bank Receipt" />;
    case ModuleId.PDC_RECEIPT: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="pdc-receipts" title="PDC Receipt" />;
    case ModuleId.SALES_VOUCHER: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="sales-voucher" title="Sales Voucher" />;
    case ModuleId.SALES_RETURN: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="sales-return" title="Sales Return" />;
    case ModuleId.PETTY_CASH_RECEIPT: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="petty-cash-receipts" title="Petty Cash Receipt" />;
    case ModuleId.BILLS_ADJUSTMENT_RECEIPT: return <BillsAdjustmentScreen theme={theme} mode="receipt" />;
    case ModuleId.SALES_UPDATION: return <SalesUpdateScreen theme={theme} />;
    case ModuleId.PDC_RECEIPT_ADJUSTMENT: return <PDCAdjustmentScreen theme={theme} mode="receipt" navigateTo={navigateTo} />;
    case ModuleId.CASH_PAYMENT: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="cash-payments" title="Cash Payment" />;
    case ModuleId.BANK_PAYMENT: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="bank-payments" title="Bank Payment" />;
    case ModuleId.PDC_PAYMENT: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="pdc-payments" title="PDC Payment" />;
    case ModuleId.PURCHASE_VOUCHER: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="purchase-voucher" title="Purchase Voucher" />;
    case ModuleId.PURCHASE_RETURN_VOUCHER: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="purchase-return" title="Purchase Return" />;
    case ModuleId.PETTY_CASH_PAYMENT: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="petty-cash-payments" title="Petty Cash Payment" />;
    case ModuleId.MATURED_PDC_VOUCHERS: return <MaturedPDCScreen theme={theme} mode="receipt" />;
    case ModuleId.PDC_PAYMENT_ADJUSTMENT: return <PDCAdjustmentScreen theme={theme} mode="payment" navigateTo={navigateTo} />;
    case ModuleId.PURCHASE_ORDER_VOUCHER: return <PurchaseOrderVoucherScreen theme={theme} />;
    case ModuleId.BILLS_ADJUSTMENT_PAYMENT: return <BillsAdjustmentScreen theme={theme} mode="payment" />;
    case ModuleId.JOURNAL_ENTRY: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="journal-voucher" title="Journal Voucher" />;
    case ModuleId.DEBIT_NOTE: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="debit-note" title="Debit Note" />;
    case ModuleId.CREDIT_NOTE: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="credit-note" title="Credit Note" />;
    case ModuleId.CASH_DEPOSIT_WITHDRAW: return <VoucherScreen theme={theme} navigateTo={navigateTo} voucherType="contra-entry" title="Contra Entry" />;
    case ModuleId.DAY_BOOK: return <DayBookScreen theme={theme} />;
    case ModuleId.CASH_BOOK: return <CashBookScreen theme={theme} />;
    case ModuleId.BANK_BOOK: return <BankBookScreen theme={theme} />;
    case ModuleId.VOUCHER_REGISTER: return <DayBookScreen theme={theme} />;
    case ModuleId.GENERAL_LEDGER_REPORT: return <FinanceReportScreen theme={theme} reportType="gl-ledger" />;
    case ModuleId.MULTIPLE_LEDGER: return <FinanceReportScreen theme={theme} reportType="multi-ledger" />;
    case ModuleId.GROUP_LEDGER_REPORT: return <FinanceReportScreen theme={theme} reportType="group-summary" />;
    case ModuleId.PAYMENT_LEDGER:
    case ModuleId.SUB_LEDGER: return <FinanceReportScreen theme={theme} reportType="gl-ledger" />;
    case ModuleId.DAYWISE_BALANCE: return <FinanceReportScreen theme={theme} reportType="daily-summary" />;
    case ModuleId.MONTHLY_BALANCE: return <FinanceReportScreen theme={theme} reportType="monthly-summary" />;
    case ModuleId.TRIAL_BALANCE: return <FinanceReportScreen theme={theme} reportType="trial-balance" />;
    case ModuleId.PROFIT_LOSS: return <FinancialStatementScreen theme={theme} statementType="PL" />;
    case ModuleId.BALANCE_SHEET:
    case ModuleId.TRADING_ACCOUNT: return <FinancialStatementScreen theme={theme} statementType="BS" />;
    case ModuleId.BANK_RECONCILIATION: return <BankReconciliationScreen theme={theme} />;
    case ModuleId.PDC_RECONCILIATION: return <PDCReconciliationScreen theme={theme} />;
    case ModuleId.CHEQUE_STATEMENT: return <FinanceReportScreen theme={theme} reportType="pdc-maturity" />;
    case ModuleId.OUTSTANDING_RECEIVABLES:
    case ModuleId.OVERDUE_RECEIVABLES:
    case ModuleId.OUTSTANDING_PAYABLES:
    case ModuleId.OVERDUE_PAYABLES: return <GeneralLedgerReportScreen theme={theme} />;

    // ─── ERP Item / Purchase ───
    case ModuleId.ITEM_CREATION: return <ItemCreationScreen theme={theme} onClose={close} />;
    case ModuleId.ITEM_CREATION_NEW: return <ItemMasterDynamic theme={theme} onClose={close} />;
    case ModuleId.ITEM_LIST:         return <ItemListScreen theme={theme} onClose={close} />;
    case ModuleId.ITEM_MASTER: return <ItemMasterDynamic theme={theme} onClose={close} />;
    case ModuleId.PURCHASE_INVOICE:
    case ModuleId.GRN:
    case ModuleId.PURCHASE_RETURN: return <PurchaseInvoiceScreen theme={theme} onClose={close} />;

    // ─── User Mgmt / System ───
    case ModuleId.USER_GROUPS: return <UserGroupsMasterScreen theme={theme} onBack={close} />;
    case ModuleId.USER_ROLES: return <UserRolesMasterScreen theme={theme} onBack={close} />;
    case ModuleId.USER_LEVELS: return <UserLevelsMasterScreen theme={theme} onBack={close} />;
    case ModuleId.USER_PROFILES: return <UserProfilesScreen theme={theme} />;
    case ModuleId.USER_CREATION: return <UserCreationMasterScreen theme={theme} onBack={close} />;
    case ModuleId.USER_PRIVILEGES: return <UserPrivilegesScreen theme={theme} />;
    case ModuleId.INDUSTRY_CONFIG: return <IndustryConfigScreen theme={theme} onBack={close} />;
    case ModuleId.ATTRIBUTE_STUDIO: return <AttributeStudioScreen theme={theme} onClose={close} />;
    case ModuleId.DYNAMIC_ATTRIBUTES: return <AttributeEngineScreen theme={theme} onClose={close} />;
    case ModuleId.HELP_ENGINE: return <HelpEngineScreen theme={theme} onClose={close} userRole={globalContextData?.role || ''} />;
    case ModuleId.TICKET_MANAGEMENT: return <TicketManagementScreen theme={theme} />;
    case ModuleId.TIER_PRIVILEGE_MATRIX: return <TierPrivilegeMatrixScreen theme={theme} onClose={close} />;
    case ModuleId.MODULE_MASTER: return <ModuleManagementScreen theme={theme} onBack={close} />;
    case ModuleId.SUBMODULE_MASTER: return <SubModuleMasterScreen theme={theme} onBack={close} />;
    case ModuleId.SCREEN_MASTER: return <ScreenMasterScreen theme={theme} onBack={close} />;
    case ModuleId.DAY_OPEN: return <BusinessDaysScreen theme={theme} mode="open" />;
    case ModuleId.DAY_CLOSE: return <BusinessDaysScreen theme={theme} mode="close" />;

    // ─── POS ───
    case ModuleId.FLOOR_MASTER: return <FloorMasterScreen theme={theme} />;
    case ModuleId.COUNTER_MASTER: return <CounterMasterScreen theme={theme} />;
    case ModuleId.PLACE_MASTER: return <PlaceMasterScreen theme={theme} />;
    case ModuleId.SHIFT_MASTER: return <ShiftMasterScreen theme={theme} />;
    case ModuleId.SALESMAN_MASTER: return <SalesmanMasterScreen theme={theme} />;
    case ModuleId.CUSTOMER_MASTER: return <CustomerMasterScreen theme={theme} />;
    case ModuleId.PAYMENT_MASTER: return <PaymentMasterScreen theme={theme} />;
    case ModuleId.PROMOTION_MASTER: return <PromotionMasterScreen theme={theme} />;
    case ModuleId.TELLER_MASTER: return <TellerMasterScreen theme={theme} />;
    case ModuleId.SALES_DIVISION_MASTER: return <SalesDivisionMasterScreen theme={theme} />;
    case ModuleId.BRANCH_WISE_PROMOTIONS: return <BranchWisePromotionsScreen theme={theme} />;
    case ModuleId.SALES_ORDER: return <SalesOrderScreen theme={theme} />;
    case ModuleId.DELIVERY_CHALLAN: return <DeliveryChallanScreen theme={theme} />;
    case ModuleId.DC_RETURNS: return <DCReturnsScreen theme={theme} />;
    case ModuleId.SALES_INVOICE: return <SalesInvoiceScreen theme={theme} isPosMaximized={isPosMaximized} setIsPosMaximized={setIsPosMaximized} />;
    case ModuleId.POS_BILLING: return <POSBillingScreen theme={theme} isPosMaximized={isPosMaximized} setIsPosMaximized={setIsPosMaximized} />;
    case ModuleId.CASH_COUNTER_BILLING: return <PosTerminalEmbed theme={theme} isPosMaximized={isPosMaximized} setIsPosMaximized={setIsPosMaximized} />;
    case ModuleId.SALES_INVOICE_RETURNS: return <PosTerminalEmbed theme={theme} isPosMaximized={isPosMaximized} setIsPosMaximized={setIsPosMaximized} />;
    case ModuleId.POS_SALES_SUMMARY: return <POSSalesSummaryReport theme={theme} />;
    case ModuleId.POS_BILL_TO_BILL: return <POSBillToBillReport theme={theme} />;

    // ─── Industry POS Screens (6.16.x) — screenId from DB sidebar ───
    case ModuleId.APPAREL_POS:     return <ApparelPOSScreen     screenId={subItemId || '6.16.11'} theme={theme} navigateTo={navigateTo} />;
    case ModuleId.SUPERMARKET_POS: return <SupermarketPOSScreen screenId={subItemId || '6.16.12'} theme={theme} navigateTo={navigateTo} />;
    case ModuleId.FRESH_VEG_POS:   return <VegCounterPOSScreen  screenId={subItemId || '6.16.13'} theme={theme} navigateTo={navigateTo} />;
    case ModuleId.FOOD_COURT_POS:  return <FoodCourtPOSScreen   screenId={subItemId || '6.16.14'} theme={theme} navigateTo={navigateTo} />;

    // ─── Purchase Invoice V2 — wired to correct DB screen IDs ────────────────
    // ─── ALL PROCUREMENT TRANSACTION SCREENS ────────────────────────────────
    // Purchase Invoice (4.10.x) → PurchaseInvoiceV2Screen
    case ModuleId.PURCHASE_INVOICE_SCREEN:
    case ModuleId.PURCHASE_INVOICE_AGST_GRC_SCREEN:
    case ModuleId.PURCHASE_INVOICE_AGST_PO_SCREEN:
    case ModuleId.PURCHASE_RETURNS_SCREEN:
    case ModuleId.PI_RETURN_NEW:
      return <PurchaseInvoiceV2Screen screenId={subItemId || moduleId} theme={theme} navigateTo={navigateTo} />;
    // GRN Screens (4.8.14/15/16) → GRNScreen
    case ModuleId.GRN_AGAINST_PO_SCREEN:
    case ModuleId.GRN_AGAINST_PI_SCREEN:
    case ModuleId.GRN_RETURN_SCREEN:
      return <GRNScreen screenId={subItemId || moduleId} theme={theme} navigateTo={navigateTo} />;
    // GRC Screens (4.8.1/2/3/5/6/8/13, 4.21.5) → GRCScreen
    case ModuleId.GRC_DIRECT_SCREEN:
    case ModuleId.GRC_AGST_PO_SCREEN:
    case ModuleId.GRC_TEMPLATE_SCREEN:
    case ModuleId.GRC_RETURNS_CHALLAN_SCREEN:
    case ModuleId.GRC_RETURN_AGST_GRC_SCREEN:
    case ModuleId.GRC_RETURNS_PACKING_SCREEN:
    case ModuleId.GRC_GARMENTS_SCREEN:
    case ModuleId.GRC_AGST_PB_SCREEN:
      return <GRCScreen screenId={subItemId || moduleId} theme={theme} navigateTo={navigateTo} />;

    // ─── Foundation ───
    case ModuleId.ENTERPRISE_ACCOUNT: return <EnterpriseAccountScreen theme={theme} />;
    case ModuleId.GROUP_COMPANY: return <GroupCompanyScreen theme={theme} />;
    case ModuleId.COMPANY_MASTER: return <CompanyMasterScreen theme={theme} />;
    case ModuleId.BRANCH_MASTER: return <BranchMasterScreen theme={theme} />;
    case ModuleId.WAREHOUSE_MASTER: return <WarehouseMasterScreen theme={theme} />;
    case ModuleId.CURRENCY_MASTER: return <CurrencyMasterScreen theme={theme} />;
    case ModuleId.FORM_BUILDER: return <ERPFormBridge theme={theme} />;

    // ─── Procurement Masters ───
    case ModuleId.SUPPLIER_MASTER: return <SupplierMasterScreen theme={theme} />;
    case ModuleId.SUPPLIER_CONTRACTS: return <SupplierContractsScreen theme={theme} />;
    case ModuleId.AGENT_MASTER: return <AgentMasterScreen theme={theme} />;
    case ModuleId.JOBBER_MASTER: return <JobberMasterScreen theme={theme} />;
    case ModuleId.TRANSPORTER_MASTER: return <TransporterScreen theme={theme} />;
    case ModuleId.TAX_GROUP_MASTER: return <TaxGroupMasterScreen theme={theme} />;
    case ModuleId.TAX_MASTER: return <TaxMasterScreen theme={theme} />;
    case ModuleId.TAILOR_MASTER: return <TailorMasterScreen theme={theme} />;
    case ModuleId.UOM_MASTER: return <UOMMasterScreen theme={theme} />;
    case ModuleId.SUB_UOM_MASTER: return <SubUOMMasterScreen theme={theme} />;
    case ModuleId.PURCHASE_OFFICER_MASTER: return <PurchaseOfficerMasterScreen theme={theme} />;
    case ModuleId.DEFAULT_VERTICALS: return <DefaultVerticalsScreen theme={theme} />;
    case ModuleId.SALES_PRICE_FORMULA: return <SalesPriceFormulaScreen theme={theme} />;
    case ModuleId.MANUFACTURER_MASTER: return <ManufacturerMasterScreen theme={theme} />;
    case ModuleId.SETTING_PRICE_LEVEL: return <SettingPriceLevelScreen theme={theme} />;
    case ModuleId.ITEM_BARCODE_GENERATION: return <ItemBarcodeGenerationScreen theme={theme} />;
    case ModuleId.HSN_CODE_MASTER: return <HSNCodeMasterScreen theme={theme} />;
    case ModuleId.DIVISION_MASTER: return <DivisionMasterScreen theme={theme} />;
    case ModuleId.DEPARTMENT_MASTER: return <DepartmentMasterScreen theme={theme} />;
    case ModuleId.CATEGORY_MASTER: return <CategoryMasterScreen theme={theme} />;
    case ModuleId.SUBCATEGORY_MASTER: return <SubCategoryScreen theme={theme} />;
    case ModuleId.BRAND_MASTER: return <BrandMasterScreen theme={theme} />;
    case ModuleId.STYLE_MASTER: return <StyleMasterScreen theme={theme} />;
    case ModuleId.SIZE_MASTER: return <SizeMasterScreen theme={theme} />;
    case ModuleId.COLOR_MASTER: return <ColorMasterScreen theme={theme} />;
    case ModuleId.DESIGN_MASTER: return <DesignMasterScreen theme={theme} />;
    case ModuleId.MATERIAL_MASTER: return <MaterialMasterScreen theme={theme} />;
    case ModuleId.MASTER_PLAN: return <MasterPlanScreen theme={theme} />;
    case ModuleId.PURCHASE_ORDER: return <PurchaseOrderScreen theme={theme} />;
    case ModuleId.PURCHASE_ORDER_CANCEL: return <PurchaseOrderCancelScreen theme={theme} />;
    case ModuleId.PURCHASE_ORDER_STATUS: return <PurchaseOrderStatusScreen theme={theme} />;
    case ModuleId.PO_PENDING_QTY_CANCEL: return <POPendingQtyCancelScreen theme={theme} />;
    case ModuleId.PO_CANCEL_REPORT: return <POCancelReportScreen theme={theme} />;
    case ModuleId.PO_SUMMARY_REPORT: return <POSummaryReportScreen theme={theme} />;
    case ModuleId.PO_SEARCH: return <POSearchScreen theme={theme} />;
    case ModuleId.PO_EXCESS_REPORT: return <POExcessReportScreen theme={theme} />;
    case ModuleId.PO_PENDING_QTY_CANCEL_REPORT: return <POPendingQtyCancelReportScreen theme={theme} />;
    case ModuleId.PROCESS_CREATION: return <ProcessCreationScreen theme={theme} />;
    case ModuleId.PRODUCTION_ORDER: return <ProductionOrderScreen theme={theme} />;
    case ModuleId.PRODUCTION_ESTIMATION: return <ProductionEstimationScreen theme={theme} />;
    case ModuleId.BILL_OF_MATERIAL: return <BillOfMaterialScreen theme={theme} />;
    case ModuleId.STOCK_ORDER: return <ProcStockOrderScreen theme={theme} />;
    case ModuleId.INWARD_JOB_WORK: return <InwardJobWorkScreen theme={theme} />;
    case ModuleId.ISSUE_TO_JOBBER: return <IssueToJobberScreen theme={theme} />;
    case ModuleId.ISSUE_TO_JOBBER_NEW: return <IssueToJobberNewScreen theme={theme} />;
    case ModuleId.RECEIPT_FROM_JOBBER: return <ReceiptFromJobberScreen theme={theme} />;
    case ModuleId.FINISHED_PRODUCT_PREPARATION: return <FinishedProductPreparationScreen theme={theme} />;
    case ModuleId.INWARD_WAREHOUSE: return <InwardWarehouseScreen theme={theme} />;
    case ModuleId.WAREHOUSE_RETURNS: return <WarehouseReturnsScreen theme={theme} />;
    case ModuleId.ISSUE_FOR_PACKAGES: return <IssueForPackagesScreen theme={theme} />;
    case ModuleId.FINISHED_PACKING_GOODS: return <FinishedPackingGoodsScreen theme={theme} />;
    case ModuleId.WASTAGE_MATERIAL: return <WastageMaterialScreen theme={theme} />;
    case ModuleId.PRINT_BARCODE_LABEL: return <PrintBarcodeLabelScreen theme={theme} />;
    case ModuleId.REPRINT_BARCODE_LABEL: return <ReprintBarcodeLabelScreen theme={theme} />;
    case ModuleId.INWARD_BARCODE_LABEL: return <InwardBarcodeLabelScreen theme={theme} />;
    case ModuleId.GOODS_RECEIPTS: return <DynamicSetupScreen screenId={subItemId||moduleId} theme={theme} />;
    case ModuleId.GRC_DIRECT: return <DynamicSetupScreen screenId={subItemId||moduleId} theme={theme} />;
    case ModuleId.GRC_GENERAL: return <DynamicSetupScreen screenId={subItemId||moduleId} theme={theme} />;
    case ModuleId.GRC_AGAINST_PO: return <DynamicSetupScreen screenId={subItemId||moduleId} theme={theme} />;
    case ModuleId.GOODS_RECEIPT_CHALLAN: return <DynamicSetupScreen screenId={subItemId||moduleId} theme={theme} />;
    case ModuleId.ITEM_WISE_PRICE_UPDATE: return <ItemWisePriceUpdateScreen theme={theme} />;
    case ModuleId.JOB_PROCESS_ISSUE_REPORT: return <JobProcessIssueReportScreen theme={theme} />;
    case ModuleId.JOBBER_RECEIPT_REPORT: return <JobberReceiptReportScreen theme={theme} />;
    case ModuleId.PROCESS_LEDGER_REPORT: return <ProcessLedgerReportScreen theme={theme} />;
    case ModuleId.PURCHASE_DISCOUNT_REPORT: return <PurchaseDiscountReportScreen theme={theme} />;
    case ModuleId.PURCHASE_INVOICE_AGST_GRC: return <PurchaseInvoiceAgstGRCScreen theme={theme} />;
    case ModuleId.PURCHASE_INVOICE_AGST_PO: return <PurchaseInvoiceAgstPOScreen theme={theme} />;
    case ModuleId.PURCHASE_INVOICE_REPORT: return <PurchaseInvoiceReportScreen theme={theme} />;
    case ModuleId.PURCHASE_RETURNS_REGISTER: return <PurchaseReturnsRegisterScreen theme={theme} />;
    case ModuleId.PURCHASE_TAX_REGISTER: return <PurchaseTaxRegisterScreen theme={theme} />;
    case ModuleId.MONTHLY_RETURNS_VAT: return <MonthlyReturnsVATScreen theme={theme} />;
    case ModuleId.PURCHASE_INVOICE_WITH_RETURNS: return <PurchaseInvoiceWithReturnsScreen theme={theme} />;
    case ModuleId.OPENING_STOCK_IMPORT_REPORT: return <OpeningStockImportReportScreen theme={theme} />;
    case ModuleId.PARTY_BILL: return <PartyBillScreen theme={theme} />;
    case ModuleId.PARTY_BILL_RETURNS: return <PartyBillReturnsScreen theme={theme} />;
    case ModuleId.PARTY_BILL_SCREEN: return <PartyBillEntryScreen theme={theme} />;
    case ModuleId.PURCHASE_INVOICE_AGST_PB: return <PurchaseInvoiceAgstPBScreen theme={theme} />;
    case ModuleId.PARTY_BILL_RETURNS_SCREEN: return <PartyBillReturnsEntryScreen theme={theme} />;
    case ModuleId.PENDING_PB_CANCEL: return <PendingPBCancelScreen theme={theme} />;
    case ModuleId.GRC_AGST_PB: return <DynamicSetupScreen screenId={subItemId||moduleId} theme={theme} />;
    case ModuleId.GRC_TEMPLATE: return <GRCTemplateScreen theme={theme} />;
    case ModuleId.GOODS_RETURNS_AGST_PO: return <GoodsReturnsAgstPOScreen theme={theme} />;
    case ModuleId.GOODS_RETURNS_AGST_GRC: return <GoodsReturnsAgstGRCScreen theme={theme} />;
    case ModuleId.GRC_PURCHASE_DISCOUNT_REPORT: return <GRCPurchaseDiscountReportScreen theme={theme} />;
    case ModuleId.GOODS_RETURNS_PACKING_REGISTER: return <GoodsReturnsPackingRegisterScreen theme={theme} />;
    case ModuleId.PURCHASE_ORDER_REGISTER: return <PurchaseOrderRegisterScreen theme={theme} />;
    case ModuleId.PURCHASE_ORDER_TEMPLATE_REGISTER: return <PurchaseOrderTemplateRegisterScreen theme={theme} />;
    case ModuleId.PURCHASE_ORDER_CANCEL_REGISTER: return <PurchaseOrderCancelRegisterScreen theme={theme} />;
    case ModuleId.SERVICE_INVOICE_REGISTER: return <ServiceInvoiceRegisterScreen theme={theme} />;
    case ModuleId.STOCK_ORDER_STATUS: return <StockOrderStatusScreen theme={theme} />;
    case ModuleId.ISSUE_TO_JOBBER_REPORT: return <IssueToJobberReportScreen theme={theme} />;
    case ModuleId.RECEIPT_FROM_JOBBER_REPORT: return <ReceiptFromJobberReportScreen theme={theme} />;
    case ModuleId.INWARD_WAREHOUSE_REPORT: return <InwardWarehouseReportScreen theme={theme} />;
    case ModuleId.FINISHED_PACKING_CONSUMPTION: return <FinishedPackingConsumptionScreen theme={theme} />;
    case ModuleId.RETURNS_FROM_JOBBER: return <ReturnsFromJobberScreen theme={theme} />;
    case ModuleId.RETURNS_FROM_JOBBER_REPORT: return <ReturnsFromJobberReportScreen theme={theme} />;
    case ModuleId.PKG_INWARD_WAREHOUSE_REPORT: return <PkgInwardWarehouseReportScreen theme={theme} />;
    case ModuleId.PKG_WAREHOUSE_RETURN_REPORT: return <PkgWarehouseReturnReportScreen theme={theme} />;
    case ModuleId.PKG_ISSUE_STOCK_REPORT: return <PkgIssueStockReportScreen theme={theme} />;
    case ModuleId.FINISHED_PACKING_GOODS_REPORT: return <FinishedPackingGoodsReportScreen theme={theme} />;
    case ModuleId.PACKING_GIFT: return <PackingGiftScreen theme={theme} />;
    case ModuleId.WASTAGE_MATERIAL_REPORT: return <WastageMaterialReportScreen theme={theme} />;
    case ModuleId.JOB_PROCESS_ISSUE: return <JobProcessIssueScreen theme={theme} />;
    case ModuleId.JOBBER_RECEIPT: return <JobberReceiptScreen theme={theme} />;
    case ModuleId.GOODS_RECEIVE_REPORT: return <GoodsReceiveReportScreen theme={theme} />;
    case ModuleId.PARTY_BILL_REPORT: return <PartyBillReportScreen theme={theme} />;
    case ModuleId.PURCHASE_INVOICE_AGST_PB_REPORT: return <PurchaseInvoiceAgstPBReportScreen theme={theme} />;
    case ModuleId.PARTY_BILL_RETURN_REPORT: return <PartyBillReturnReportScreen theme={theme} />;
    case ModuleId.PENDING_PB_CANCEL_REPORT: return <PendingPBCancelReportScreen theme={theme} />;
    case ModuleId.PARTY_BILL_STATUS_REPORT: return <PartyBillStatusReportScreen theme={theme} />;
    case ModuleId.PARTY_BILL_WISE_AGEING_REPORT: return <PartyBillWiseAgeingReportScreen theme={theme} />;
    case ModuleId.GRC_AGST_PB_REPORT: return <GRCAgstPBReportScreen theme={theme} />;

    // ─── Inventory ───
    case ModuleId.STOCK_VERIFY_SCHEDULE: return <StockVerifyScheduleScreen theme={theme} />;
    case ModuleId.STOCK_VERIFY_ENTRY: return <StockVerifyEntryScreen theme={theme} />;
    case ModuleId.VERIFY_UPDATE_STOCK: return <VerifyUpdateStockScreen theme={theme} />;
    case ModuleId.SHORTAGE_STOCK: return <ShortageStockScreen theme={theme} />;
    case ModuleId.EXCESS_STOCK: return <ExcessStockScreen theme={theme} />;
    case ModuleId.SHORTAGE_EXCESS_REPORT: return <ShortageExcessReportScreen theme={theme} />;
    case ModuleId.STOCK_VERIFY_ENTRY_MANUAL: return <StockVerifyEntryManualScreen theme={theme} />;
    case ModuleId.PHYSICAL_STOCK_PDA: return <PhysicalStockPDAScreen theme={theme} />;
    case ModuleId.PHYSICAL_STOCK_SCAN_REPORT: return <PhysicalStockScanReportScreen theme={theme} />;
    case ModuleId.ROL_SETTINGS_NEW: return <ROLSettingsNewScreen theme={theme} />;
    case ModuleId.REORDER_LEVEL_REPORT: return <ReOrderLevelReportScreen theme={theme} />;
    case ModuleId.PRICE_UPDATE: return <PriceUpdateScreen theme={theme} />;
    case ModuleId.TOKEN_UPDATE: return <TokenUpdateScreen theme={theme} />;
    case ModuleId.BULK_PRODUCT_UPDATION: return <BulkProductUpdationScreen theme={theme} />;
    case ModuleId.OEM_CODE_UPDATE: return <OEMCodeUpdateScreen theme={theme} />;
    case ModuleId.GENERAL_TOKEN_UPDATE: return <GeneralTokenUpdateScreen theme={theme} />;
    case ModuleId.ITEM_CONVERSION: return <ItemConversionScreen theme={theme} />;
    case ModuleId.ITEM_CONVERSION_REPORT: return <ItemConversionReportScreen theme={theme} />;
    case ModuleId.TALLY_INTEGRATION_AGST_BILLS: return <TallyIntegrationAgstBillsScreen theme={theme} />;
    case ModuleId.ASSIGN_TO_FLOOR_RACK: return <AssignToFloorRackScreen theme={theme} />;
    case ModuleId.HSN_CODE_UPDATE: return <HSNCodeUpdateScreen theme={theme} />;
    case ModuleId.PRICE_PRINT_BARCODE: return <PricePrintBarcodeScreen theme={theme} />;
    case ModuleId.PRICE_REPRINT_BARCODE: return <PriceReprintBarcodeScreen theme={theme} />;
    case ModuleId.STOCK_MOVEMENT: return <StockMovementScreen theme={theme} />;
    case ModuleId.MONTHLY_STOCK_MOVEMENT: return <MonthlyStockMovementScreen theme={theme} />;
    case ModuleId.STOCK_LEDGER: return <StockLedgerScreen theme={theme} />;
    case ModuleId.CLOSING_STOCK: return <ClosingStockScreen theme={theme} />;
    case ModuleId.STOCK_ANALYSIS_REPORT: return <StockAnalysisReportScreen theme={theme} />;
    case ModuleId.PILOT_STOCK_SALES_REPORT: return <PilotStockSalesReportScreen theme={theme} />;
    case ModuleId.BARCODE_ITEM_INFORMATION: return <BarcodeItemInformationScreen theme={theme} />;
    case ModuleId.PRICE_UPDATE_REPORT: return <PriceUpdateReportScreen theme={theme} />;
    case ModuleId.TOKEN_UPDATE_REPORT: return <TokenUpdateReportScreen theme={theme} />;
    case ModuleId.BULK_PRODUCT_UPDATION_REPORT: return <BulkProductUpdationReportScreen theme={theme} />;
    case ModuleId.OEM_CODE_UPDATION_REPORT: return <OEMCodeUpdationReportScreen theme={theme} />;
    case ModuleId.PACKED_ITEMS_STOCK_REPORT: return <PackedItemsStockReportScreen theme={theme} />;
    case ModuleId.CLOSING_STOCK_WITH_PURCHASE: return <ClosingStockWithPurchaseScreen theme={theme} />;
    case ModuleId.CLOSING_STOCK_AUDITOR: return <ClosingStockAuditorScreen theme={theme} />;
    case ModuleId.STOCK_LEDGER_WITH_DC: return <StockLedgerWithDCScreen theme={theme} />;
    case ModuleId.CLOSING_STOCK_WITH_DC: return <ClosingStockWithDCScreen theme={theme} />;
    case ModuleId.CLOSING_STOCK_IMAGE_REPORT: return <ClosingStockImageReportScreen theme={theme} />;
    case ModuleId.FLOOR_RACK_REPORT: return <FloorRackReportScreen theme={theme} />;
    case ModuleId.FLOOR_WISE_CLOSING_STOCK_REPORT: return <FloorWiseClosingStockReportScreen theme={theme} />;
    case ModuleId.STOCK_TRACKING_REPORT: return <StockTrackingReportScreen theme={theme} />;
    case ModuleId.FINANCIAL_YEAR_DETAIL_REPORT: return <FinancialYearDetailReportScreen theme={theme} />;
    case ModuleId.CUSTOMER_WISE_FAST_MOVING_SALES: return <CustomerWiseFastMovingSalesScreen theme={theme} />;
    case ModuleId.PURCHASE_SUMMARY_REPORTS: return <PurchaseSummaryReportsScreen theme={theme} />;
    case ModuleId.PURCHASE_PARTY_INFO: return <PurchasePartyInfoScreen theme={theme} />;
    case ModuleId.INVOICE_WISE_OUTSTANDING_REPORT: return <InvoiceWiseOutstandingReportScreen theme={theme} />;
    case ModuleId.SUPPLIER_WISE_AGEING_REPORT: return <SupplierWiseAgeingReportScreen theme={theme} />;
    case ModuleId.SOR_SUPPLIER_WISE_SALES_REPORT: return <SORSupplierWiseSalesReportScreen theme={theme} />;
    case ModuleId.CITY_WISE_OUTSTANDING_PAYABLES: return <CityWiseOutstandingPayablesScreen theme={theme} />;
    case ModuleId.CITY_WISE_OUTSTANDING_RECEIVABLES: return <CityWiseOutstandingReceivablesScreen theme={theme} />;
    case ModuleId.OPENING_STOCK_IMPORT: return <OpeningStockImportScreen theme={theme} />;
    case ModuleId.OPENING_STOCK_IMPORT_WITH_SUPPLIER: return <OpeningStockImportWithSupplierScreen theme={theme} />;
    case ModuleId.MATERIAL_RECEIPT_IMPORT_DIRECT: return <MaterialReceiptImportDirectScreen theme={theme} />;
    case ModuleId.STOCK_EXPORT_TO_BRANCH: return <StockExportToBranchScreen theme={theme} />;
    case ModuleId.STOCK_RETURN_FROM_BRANCH_OFFLINE: return <StockReturnFromBranchOfflineScreen theme={theme} />;
    case ModuleId.STOCK_RETURN_FROM_BRANCH_ONLINE: return <StockReturnFromBranchOnlineScreen theme={theme} />;
    case ModuleId.STOCK_RETURN_IMPORT_AT_WAREHOUSE: return <StockReturnImportAtWarehouseScreen theme={theme} />;
    case ModuleId.STOCK_RETURN_TO_WAREHOUSE: return <StockReturnToWarehouseScreen theme={theme} />;
    case ModuleId.STOCK_IMPORT_FROM_WAREHOUSE_OFFLINE: return <StockImportFromWarehouseOfflineScreen theme={theme} />;
    case ModuleId.STOCK_IMPORT_FROM_WAREHOUSE_ONLINE: return <StockImportFromWarehouseOnlineScreen theme={theme} />;
    case ModuleId.BRANCH_OPENING_STOCK_IMPORT: return <BranchOpeningStockImportScreen theme={theme} />;
    case ModuleId.SALES_EXPORT_TO_WAREHOUSE: return <SalesExportToWarehouseScreen theme={theme} />;
    case ModuleId.SALES_EXPORT_TO_EXCEL: return <SalesExportToExcelScreen theme={theme} />;
    case ModuleId.SALES_IMPORT_FROM_EXCEL: return <SalesImportFromExcelScreen theme={theme} />;
    case ModuleId.PHYSICAL_STOCK_EXPORT_TO_WH: return <PhysicalStockExportToWHScreen theme={theme} />;
    case ModuleId.BRANCH_STOCK_MOVEMENT: return <BranchStockMovementScreen theme={theme} />;
    case ModuleId.BRANCH_WISE_STOCK: return <BranchWiseStockScreen theme={theme} />;
    case ModuleId.STOCK_TRANSFER_REPORT: return <StockTransferReportScreen theme={theme} />;
    case ModuleId.BRANCH_POS_INFO: return <BranchPOSInfoScreen theme={theme} />;
    case ModuleId.SUPPLIER_WISE_PURCHASE_ORDER_REPORT: return <SupplierWisePurchaseOrderReportScreen theme={theme} />;
    case ModuleId.BRANCH_BILL_TO_BILL_REPORT: return <BranchBillToBillReportScreen theme={theme} />;
    case ModuleId.BRANCH_BULK_INVOICE_PRINT_REPORT: return <BranchBulkInvoicePrintReportScreen theme={theme} />;
    case ModuleId.BRANCH_DAY_SHEET_REPORT: return <BranchDaySheetReportScreen theme={theme} />;
    case ModuleId.BRANCH_POS_CANCEL_REPORT: return <BranchPOSCancelReportScreen theme={theme} />;
    case ModuleId.BRANCH_TAX_REGISTER: return <BranchTaxRegisterScreen theme={theme} />;
    case ModuleId.BRANCH_CUSTOMER_SALES_REPORT: return <BranchCustomerSalesReportScreen theme={theme} />;
    case ModuleId.BRANCH_PRODUCT_AGEING: return <BranchProductAgeingScreen theme={theme} />;
    case ModuleId.BRANCH_FAST_MOVING_ITEMS: return <BranchFastMovingItemsScreen theme={theme} />;
    case ModuleId.BRANCH_EOD_REPORT: return <BranchEODReportScreen theme={theme} />;
    case ModuleId.STOCK_EXPORT_TO_FRANCHISE: return <StockExportToFranchiseScreen theme={theme} />;
    case ModuleId.STOCK_IMPORT_FROM_FRANCHISE: return <StockImportFromFranchiseScreen theme={theme} />;
    case ModuleId.STOCK_IMPORT_AT_FRANCHISE_ONLINE: return <StockImportAtFranchiseOnlineScreen theme={theme} />;
    case ModuleId.FRANCHISE_BARCODE_PRINT: return <FranchiseBarcodePrintScreen theme={theme} />;
    case ModuleId.STOCK_RETURN_AT_FRANCHISE_ONLINE: return <StockReturnAtFranchiseOnlineScreen theme={theme} />;
    case ModuleId.FRANCHISE_SALES_IMPORT: return <FranchiseSalesImportScreen theme={theme} />;
    case ModuleId.STOCK_EXPORT_REPORT: return <StockExportReportScreen theme={theme} />;
    case ModuleId.FRANCHISE_SALES_SUMMARY: return <FranchiseSalesSummaryScreen theme={theme} />;
    case ModuleId.FRANCHISE_STOCK_AND_SALES_REPORT: return <FranchiseStockAndSalesReportScreen theme={theme} />;
    case ModuleId.FRANCHISE_SALES: return <FranchiseSalesScreen theme={theme} />;
    case ModuleId.GROUP_WISE_FRANCHISE_SALES: return <GroupWiseFranchiseSalesScreen theme={theme} />;
    case ModuleId.FRANCHISE_PRODUCT_PROFITABILITY: return <FranchiseProductProfitabilityScreen theme={theme} />;
    case ModuleId.FRANCHISE_INVOICE_REPORT: return <FranchiseInvoiceReportScreen theme={theme} />;
    case ModuleId.FRANCHISE_MARKDOWN_PROFIT_REPORT: return <FranchiseMarkdownProfitReportScreen theme={theme} />;
    case ModuleId.STOCK_TRANSFER_TO_SHOP: return <StockTransferToShopScreen theme={theme} />;
    case ModuleId.STOCK_RETURN_FROM_SHOP: return <StockReturnFromShopScreen theme={theme} />;
    case ModuleId.SHOP_BARCODE_PRINT: return <ShopBarcodePrintScreen theme={theme} />;
    case ModuleId.SALES_IMPORT_FROM_SHOP: return <SalesImportFromShopScreen theme={theme} />;
    case ModuleId.SHOP_STOCK_TRANSFER_REPORT: return <ShopStockTransferReportScreen theme={theme} />;
    case ModuleId.SIS_POS_INFO: return <SISPOSInfoScreen theme={theme} />;
    case ModuleId.SIS_SALES: return <SISSalesScreen theme={theme} />;
    case ModuleId.PRODUCT_PROFITABILITY: return <ProductProfitabilityScreen theme={theme} />;
    case ModuleId.PRODUCT_AGEING: return <ProductAgeingScreen theme={theme} />;
    case ModuleId.FAST_MOVING_ITEMS: return <FastMovingItemsScreen theme={theme} />;
    case ModuleId.EXPIRY_ITEMS: return <ExpiryItemsScreen theme={theme} />;
    case ModuleId.SUPPLIER_PERFORMANCE: return <SupplierPerformanceScreen theme={theme} />;
    case ModuleId.PURCHASE_PROFITABILITY: return <PurchaseProfitabilityScreen theme={theme} />;
    case ModuleId.MRP_WISE_PURCHASE_SALES_REPORT: return <MRPWisePurchaseSalesReportScreen theme={theme} />;
    case ModuleId.SM_PRODUCT_PROFITABILITY: return <SMProductProfitabilityScreen theme={theme} />;
    case ModuleId.PRODUCT_PROFITABILITY_NEW: return <ProductProfitabilityNewScreen theme={theme} />;
    case ModuleId.INVENTORY_DIVISION_MASTER: return <InventoryDivisionMasterScreen theme={theme} />;
    case ModuleId.INVENTORY_SIZE_MASTER: return <InventorySizeMasterScreen theme={theme} />;
    case ModuleId.INVENTORY_STYLE_MASTER: return <InventoryStyleMasterScreen theme={theme} />;
    case ModuleId.INVENTORY_DEPARTMENT_MASTER: return <InventoryDepartmentMasterScreen theme={theme} />;
    case ModuleId.SUB_CATEGORY_MASTER: return <SubCategoryMasterScreen theme={theme} />;
    case ModuleId.INVENTORY_UOM_MASTER: return <InventoryUOMMasterScreen theme={theme} />;
    case ModuleId.INVENTORY_SUB_UOM_MASTER: return <InventorySubUOMMasterScreen theme={theme} />;
    case ModuleId.RECEIPT_FROM_BRANCH: return <ReceiptFromBranchScreen theme={theme} />;
    case ModuleId.STOCK_IMPORT_FROM_BRANCH: return <StockImportFromBranchScreen theme={theme} />;
    case ModuleId.BRANCH_STOCK_IMPORT_FROM_WAREHOUSE: return <BranchStockImportFromWarehouseScreen theme={theme} />;
    case ModuleId.ISSUE_TO_WAREHOUSE_REPORT: return <IssueToWarehouseReportScreen theme={theme} />;
    case ModuleId.RETURN_FROM_WAREHOUSE_REPORT: return <ReturnFromWarehouseReportScreen theme={theme} />;
    case ModuleId.RECEIPT_AT_BRANCH_REPORT: return <ReceiptAtBranchReportScreen theme={theme} />;
    case ModuleId.ITEM_MOVEMENT_REPORT: return <ItemMovementReportScreen theme={theme} />;
    case ModuleId.ITEM_BARCODE_WISE_STOCK_REPORT: return <ItemBarcodeWiseStockReportScreen theme={theme} />;
    case ModuleId.STOCK_AGING_REPORT: return <StockAgingReportScreen theme={theme} />;

    default:
      if (subItemId) return <DynamicSetupScreen moduleId={moduleId} subItemId={subItemId} title={title} theme={theme} onClose={close} />;
      return <ModuleSelector onSelect={navigateTo} theme={theme} userRole={globalContextData?.role || ''} />;
  }
  }
}
